import { useState } from 'react';
import { Camera, Check, ChevronDown, X } from 'lucide-react';
import type { Station } from '@hcc/types';
import { PhotoCaptureModal } from '../person/PhotoCaptureModal';
import { AdditionalInfoEditor } from './AdditionalInfoEditor';
import type { ConfirmRow } from './useKitConfirmItems';

/**
 * One row in the confirm-on-arrival panel: the asset identity plus a
 * Confirmed / Not present outcome. The detail (editable identifiers, last
 * service date, condition photo, additional info) expands independently of
 * the outcome, so it can be opened and pre-filled before an outcome is set.
 * Choosing Confirmed also opens it as a convenience.
 */
export function ConfirmItemRow({
  row,
  station,
  onOutcome,
  onServiceDate,
  onPhoto,
  onSerial,
  onRegistration,
  onAttributes,
}: {
  row: ConfirmRow;
  station: Station;
  onOutcome: (outcome: 'confirmed' | 'not_present') => void;
  onServiceDate: (date: string) => void;
  onPhoto: (dataUrl: string | undefined) => void;
  onSerial: (value: string) => void;
  onRegistration: (value: string) => void;
  onAttributes: (attrs: Record<string, string>) => void;
}) {
  const [cameraOpen, setCameraOpen] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const serial = row.serial;
  const registration = row.registration;
  // Registration only makes sense for vehicles: show its field on Fleet or
  // when the asset already carries one.
  const showRegistration = station === 'Fleet' || registration !== undefined;
  // Service date, photo and additional info describe an item the person
  // holds, so they don't apply once it's marked not present.
  const showConfirmFields = row.outcome !== 'not_present';

  return (
    <div className="rounded-token-sm border border-line/10 bg-surface p-3">
      <div className="flex items-start justify-between gap-3">
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex min-w-0 items-start gap-2 text-left"
        >
          <ChevronDown
            className={`mt-0.5 h-4 w-4 shrink-0 text-fg-muted transition-transform ${
              expanded ? '' : '-rotate-90'
            }`}
          />
          <div className="min-w-0">
            <div className="truncate text-sm font-medium text-fg">{row.asset.description}</div>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-fg-muted">
              <span className="font-mono">{row.asset.asset_tag}</span>
              {serial ? <span className="font-mono">SN {serial}</span> : null}
              {registration ? <span className="font-mono">{registration}</span> : null}
            </div>
          </div>
        </button>
        <div className="flex shrink-0 overflow-hidden rounded-token-sm border border-line/10">
          <button
            type="button"
            onClick={() => {
              onOutcome('confirmed');
              setExpanded(true);
            }}
            className={`inline-flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium transition-colors ${
              row.outcome === 'confirmed'
                ? 'bg-success-soft/15 text-success-soft-fg'
                : 'bg-surface text-fg-muted hover:bg-surface-2'
            }`}
          >
            <Check className="h-3.5 w-3.5" />
            Confirmed
          </button>
          <button
            type="button"
            onClick={() => onOutcome('not_present')}
            className={`inline-flex items-center gap-1 border-l border-line/10 px-2.5 py-1.5 text-xs font-medium transition-colors ${
              row.outcome === 'not_present'
                ? 'bg-danger-soft/15 text-danger-soft-fg'
                : 'bg-surface text-fg-muted hover:bg-surface-2'
            }`}
          >
            <X className="h-3.5 w-3.5" />
            Not present
          </button>
        </div>
      </div>

      {expanded ? (
        <div className="mt-3 flex flex-wrap items-center gap-3 border-t border-divider/10 pt-3">
          {/* Identifiers are pre-filled from the register but editable: the
              import may have no serial, or the wrong one. Leave as-is when
              it's already correct. */}
          <label className="text-xs text-fg-muted">
            Serial
            <input
              type="text"
              value={serial ?? ''}
              onChange={(e) => onSerial(e.target.value)}
              placeholder="add if missing"
              className="ml-2 w-40 rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1 font-mono text-sm text-fg focus:border-accent focus:outline-none"
            />
          </label>
          {showRegistration ? (
            <label className="text-xs text-fg-muted">
              Reg
              <input
                type="text"
                value={registration ?? ''}
                onChange={(e) => onRegistration(e.target.value)}
                placeholder="add if missing"
                className="ml-2 w-32 rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1 font-mono text-sm text-fg focus:border-accent focus:outline-none"
              />
            </label>
          ) : null}
          {showConfirmFields ? (
            <label className="text-xs text-fg-muted">
              Last serviced
              <input
                type="date"
                value={row.last_service_date ?? ''}
                onChange={(e) => onServiceDate(e.target.value)}
                className="ml-2 rounded-token-sm border border-line-strong/20 bg-surface px-2 py-1 text-sm text-fg focus:border-accent focus:outline-none"
              />
            </label>
          ) : null}
          {showConfirmFields ? (
            row.photoDataUrl ? (
              <div className="flex items-center gap-2">
                <img
                  src={row.photoDataUrl}
                  alt={`Condition of ${row.asset.description}`}
                  className="h-9 w-9 rounded-token-sm object-cover"
                />
                <button
                  type="button"
                  onClick={() => setCameraOpen(true)}
                  className="text-xs font-medium text-accent hover:underline"
                >
                  Retake
                </button>
                <button
                  type="button"
                  onClick={() => onPhoto(undefined)}
                  className="text-xs font-medium text-fg-muted hover:underline"
                >
                  Remove
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setCameraOpen(true)}
                className="inline-flex items-center gap-1.5 rounded-token-sm border border-line-strong/20 px-2.5 py-1.5 text-xs font-medium text-fg-muted transition-colors hover:bg-surface-2"
              >
                <Camera className="h-3.5 w-3.5" />
                Add condition photo
              </button>
            )
          ) : null}
          {showConfirmFields ? (
            <div className="basis-full">
              <AdditionalInfoEditor value={row.attributes} onChange={onAttributes} />
            </div>
          ) : null}
          {!showConfirmFields ? (
            <p className="basis-full text-[11px] text-fg-muted">
              Marked not present. Only the identifier is kept; it will be flagged to chase.
            </p>
          ) : null}
        </div>
      ) : null}

      <PhotoCaptureModal
        open={cameraOpen}
        personName={row.asset.description}
        eyebrow="Condition photo"
        title={`Condition photo, ${row.asset.description}`}
        hint="Photograph the item's current condition. Optional, retake if you need a clearer shot."
        onClose={() => setCameraOpen(false)}
        onSave={(dataUrl) => {
          onPhoto(dataUrl);
          setCameraOpen(false);
        }}
      />
    </div>
  );
}
